// src/components/mockExam/ExamShowcase.jsx
//
// The scroll story at the top of /:lang/mock-exams/:slug. One stage is pinned
// under the header while the page scrolls past invisible markers; each marker
// that crosses the middle of the viewport switches the stage to the next
// chapter: 00 is the hero (h1, intro, CTA) with the exam's cover sheet, then one
// chapter per exam part, replayed from a real paper (src/data/examShowcase.js).
//
// Nothing here hijacks scrolling: the page scrolls normally and the stage only
// reads where the markers are. All chapter captions stay in the DOM (only their
// opacity changes), so crawlers and screen readers get every heading in order;
// the animated exam frame is decorative and aria-hidden.
//
// Sticky needs `overflow-x: clip` (not `hidden`) on html/body; see index.css.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown, Clock, Volume2 } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.025em' };
const NUMERAL = { fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.05em' };
const TICK_MS = 100;
const TICK_MAX = 200;
/** Stage height: the viewport minus the scrolled public header (h-14). */
const STAGE_H = 'calc(100svh - 3.5rem)';
/** Scroll distance per chapter, in svh. */
const MARKER_SVH = 80;

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const easeOut = (k) => 1 - Math.pow(1 - Math.min(1, Math.max(0, k)), 3);

/** Inline style for a stacked layer: the active one shows, the others wait
 *  above (already seen) or below (still to come), slightly blurred. */
const layerStyle = (i, active) =>
  i === active
    ? { opacity: 1, transform: 'none', filter: 'none' }
    : {
        opacity: 0,
        transform: `translateY(${i < active ? -24 : 24}px)`,
        filter: 'blur(4px)',
        pointerEvents: 'none',
      };

const LAYER_TRANSITION =
  'transition-[opacity,transform,filter] duration-[650ms] ease-[cubic-bezier(.2,.7,.2,1)]';

/* ── Scenes ───────────────────────────────────────────────────────────────── */

function CoverScene({ exam, t, time }) {
  const k = easeOut((time - 2) / 12);
  const stats = [
    [exam.totalMinutes, t('mockExam.stats.duration', { defaultValue: 'Minutes' })],
    [exam.totalPoints, t('mockExam.stats.points', { defaultValue: 'Total points' })],
    [exam.sections.length, t('mockExam.stats.sections', { defaultValue: 'Sections' })],
    [exam.variants, t('mockExam.stats.papers', { defaultValue: 'Modelltests' })],
  ];
  const rowIn = (n) => (time >= n ? { opacity: 1, transform: 'none' } : { opacity: 0, transform: 'translateY(6px)' });
  const shared = t('mockExam.format.shared', { defaultValue: 'shared clock' });
  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4 pb-4 lg:pb-5 border-b border-stone-100">
        {stats.map(([value, label]) => (
          <div key={label}>
            <div className="text-[2.1rem] lg:text-[3rem] leading-[0.9] font-bold tabular-nums" style={NUMERAL}>
              {Math.round(value * k)}
            </div>
            <div className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-stone-500">{label}</div>
          </div>
        ))}
      </div>
      <div className="pt-1">
        {exam.sections.map((s, i) => (
          <div
            key={s.id}
            className={`grid grid-cols-[1.2fr_1fr] lg:grid-cols-[1.1fr_1fr_1fr] gap-3 py-2 lg:py-2.5 text-[13.5px] lg:text-[14px] transition-[opacity,transform] duration-500 ${i > 0 ? 'border-t border-stone-100' : ''}`}
            style={rowIn(6 + i * 2)}
          >
            <span className="font-semibold">{s.de}</span>
            <span className="hidden lg:block text-stone-500">{s.en}</span>
            <span className="text-right text-stone-500 tabular-nums">
              {s.minutes > 0 ? `${s.minutes} min` : shared} · {s.points} P
            </span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[13px] leading-snug text-stone-500 transition-opacity duration-500" style={rowIn(18)}>
        {exam.passing === 'modular'
          ? t('mockExam.format.noteModular', {
              pass: 60,
              defaultValue: 'Pass mark: {{pass}}%. Each module is scored independently and can be retaken on its own.',
            })
          : t('mockExam.format.noteSeparate', {
              pass: 60,
              defaultValue: 'Pass mark: {{pass}}%. The written and oral parts are graded separately and each must reach {{pass}}%.',
            })}
      </p>
      <div className="mt-auto pt-3 flex justify-center max-lg:[@media(max-height:700px)]:hidden">
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-stone-500">
          {t('mockExam.showcase.scrollHint', { defaultValue: 'Scroll for Teil 1' })}
          <ChevronDown className="w-3.5 h-3.5 showcase-nudge" />
        </span>
      </div>
    </div>
  );
}

const LABEL = 'text-[11px] lg:text-[12px] font-semibold uppercase tracking-[0.08em] text-stone-500';
/** Never shrink a scene below this; past it the bottom fade takes over. */
const MIN_FIT = 0.7;

/**
 * Phones show the answer(s) plus `others` distractors; desktop shows every option.
 * Returns a className per option id.
 */
function phoneKeep(options, answers, others) {
  const keep = new Set(answers);
  options.forEach((o) => {
    if (!keep.has(o.id) && keep.size < answers.length + others) keep.add(o.id);
  });
  return (id) => (keep.has(id) ? '' : 'hidden lg:flex');
}

/**
 * Scales a scene down to fit its frame on short screens, widening it as it
 * shrinks so it still fills the width. Layout only; the scene never scrolls.
 */
function FitBox({ children }) {
  const outer = useRef(null);
  const inner = useRef(null);
  const [k, setK] = useState(1);
  const shrinkRef = useRef(() => {});
  useLayoutEffect(() => {
    const o = outer.current;
    const n = inner.current;
    if (!o || !n || typeof ResizeObserver === 'undefined') return undefined;
    // Shrink only, in whole 2 % steps. Shrinking widens the scene, which makes
    // it shorter, so this settles in a few steps; growing in the same loop
    // would undo that and oscillate. Runs once per frame, never synchronously,
    // so six scenes adjusting at once cannot pile up nested renders.
    let frame = 0;
    const shrink = () => {
      frame = 0;
      setK((cur) => {
        const need = n.scrollHeight * cur;
        const have = o.clientHeight;
        if (need <= have + 2 || cur <= MIN_FIT) return cur;
        return Math.max(MIN_FIT, Math.floor(((cur * have) / need) * 50) / 50);
      });
    };
    shrinkRef.current = () => {
      if (!frame) frame = requestAnimationFrame(shrink);
    };
    // The frame changed size (rotation, resize): start again from full size.
    let lastHeight = o.clientHeight;
    const ro = new ResizeObserver(() => {
      if (Math.abs(o.clientHeight - lastHeight) < 1) return;
      lastHeight = o.clientHeight;
      setK(1);
    });
    ro.observe(o);
    shrinkRef.current();
    return () => {
      ro.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  // Content grows as a scene plays (an answer turns bold, a letter types), which
  // no ResizeObserver sees because the box itself is fixed. Re-check after each render.
  useLayoutEffect(() => shrinkRef.current());
  return (
    <div ref={outer} className="h-full overflow-hidden">
      <div
        ref={inner}
        className="origin-top-left overflow-hidden"
        style={{ width: `${100 / k}%`, height: `${100 / k}%`, transform: k < 1 ? `scale(${k})` : undefined }}
      >
        {children}
      </div>
    </div>
  );
}

/** A filled answer slot (letter) or an empty dashed one. */
function Slot({ filled, value, size = 'w-10 h-10 text-[17px]' }) {
  return filled ? (
    <span className={`showcase-pop ${size} shrink-0 rounded-xl bg-[#006D77] text-white inline-flex items-center justify-center font-bold`} style={DISPLAY}>
      {value}
    </span>
  ) : (
    <span className={`${size} shrink-0 rounded-xl border-2 border-dashed border-stone-300`} />
  );
}

/** One option row in a list; `state` is idle | hover | picked | used. */
function OptionRow({ id, text, state, className = '' }) {
  const styles = {
    idle: { borderColor: '#E7E5E4', background: '#fff', opacity: 1 },
    hover: { borderColor: '#006D77', background: '#E6F2F3', opacity: 1 },
    picked: { borderColor: '#006D77', background: '#E6F2F3', opacity: 1 },
    used: { borderColor: '#E7E5E4', background: '#fff', opacity: 0.4 },
  };
  return (
    <div
      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl border text-[13px] lg:text-[14px] leading-snug transition-[background-color,border-color,opacity] duration-300 ${className}`}
      style={styles[state]}
    >
      <span className="w-6 h-6 shrink-0 rounded-lg bg-stone-100 inline-flex items-center justify-center text-[12px] font-bold" style={DISPLAY}>
        {id}
      </span>
      {text}
    </div>
  );
}

const WAVE = Array.from({ length: 30 }, (_, i) => ({
  h: 12 + Math.round(Math.abs(Math.sin(i * 1.7)) * 24),
  d: (0.7 + (i % 5) * 0.12).toFixed(2),
  delay: ((i * 0.07) % 0.9).toFixed(2),
}));

/** The exam's audio bar: no pause control, because the real player has none. */
function AudioBar({ audio, time, playing }) {
  return (
    <>
      <div className="flex items-center gap-3 p-3 lg:p-4 rounded-2xl bg-stone-900 text-white">
        <span className="w-10 h-10 lg:w-12 lg:h-12 shrink-0 rounded-full bg-white text-stone-900 inline-flex items-center justify-center">
          <Volume2 className="w-4 h-4 lg:w-5 lg:h-5" />
        </span>
        <div className="hidden lg:flex flex-col shrink-0 max-w-[40%]">
          <span className="text-[14px] font-semibold truncate">{audio.label}</span>
          <span className="text-[12px] text-stone-300">{audio.note}</span>
        </div>
        <div className="flex-1 flex items-center justify-center gap-[3px] h-10 overflow-hidden">
          {WAVE.map((b, i) => (
            <span
              key={i}
              className="showcase-wave block w-1 shrink-0 rounded bg-[#d97706]"
              style={{
                height: b.h,
                animationDuration: `${b.d}s`,
                animationDelay: `${b.delay}s`,
                animationPlayState: playing ? 'running' : 'paused',
              }}
            />
          ))}
        </div>
        <span className="text-[13px] font-bold tabular-nums text-stone-200" style={DISPLAY}>
          {mmss(Math.floor(Math.min(time, TICK_MAX) / 10))}
        </span>
      </div>
      <div className="lg:hidden mt-2 text-[12px] text-stone-500">{audio.label} · {audio.note}</div>
    </>
  );
}

/** Überschriften to a text: one heading lights up, then drops into the slot. */
function MatchingScene({ c, time }) {
  const pick = time >= 10;
  const placed = time >= 20;
  const answer = c.options.find((o) => o.id === c.answer);
  const onPhone = phoneKeep(c.options, [c.answer], 2);
  return (
    <div className="h-full grid lg:grid-cols-2 gap-4 lg:gap-6 content-start">
      <div className="flex flex-col gap-3">
        <div className={LABEL}>{c.textLabel}</div>
        <p className="text-[14px] lg:text-[15px] leading-relaxed text-stone-800 line-clamp-3 lg:line-clamp-none">{c.text}</p>
        <div className="flex items-center gap-3 py-2.5 border-y border-stone-100 lg:mt-auto">
          <span className="text-[13px] font-semibold">Überschrift</span>
          <Slot filled={placed} value={c.answer} />
          {placed && (
            <span className="showcase-pop hidden lg:inline text-[13px] text-stone-500" style={{ animationDelay: '.15s' }}>
              {answer?.text}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1.5 lg:gap-2">
        <div className={`hidden lg:block mb-1 ${LABEL}`}>{c.optionsLabel}</div>
        {c.options.map((o) => {
          const on = o.id === c.answer;
          return <OptionRow key={o.id} id={o.id} text={o.text} className={onPhone(o.id)} state={on && placed ? 'used' : on && pick ? 'hover' : 'idle'} />;
        })}
      </div>
    </div>
  );
}

/**
 * One gap in a text. With `options` the answer is picked (inline row for words,
 * stacked list for whole sentences); without, it is typed letter by letter.
 */
function GapScene({ c, time }) {
  const typed = !c.options;
  const hover = time >= 8;
  const done = time >= 16;
  const fill = typed
    ? c.answer.slice(0, Math.max(0, Math.min(c.answer.length, time - 6)))
    : c.options.find((o) => o.id === c.answer)?.text;
  const showFill = typed ? fill.length > 0 : done;
  const stacked = c.layout === 'stack';
  const onPhone = typed ? () => '' : phoneKeep(c.options, [c.answer], stacked ? 1 : c.options.length);
  return (
    <div className="h-full flex flex-col gap-3 lg:gap-4">
      {c.label && <div className={LABEL}>{c.label}</div>}
      <div className="rounded-2xl border border-stone-200 bg-stone-50 p-3.5 lg:p-5 text-[14px] lg:text-[15.5px] leading-[1.85] text-stone-800">
        {c.before}{' '}
        {showFill ? (
          <span className={`${typed ? '' : 'showcase-pop '}inline px-1.5 rounded-md bg-[#E6F2F3] text-[#006D77] font-bold border-b-2 border-[#006D77]`}>
            {fill}
            {typed && fill.length < c.answer.length && <span className="showcase-caret" />}
          </span>
        ) : (
          <span className="inline-block min-w-[72px] text-center border-b-2 border-stone-900 text-[12px] font-bold text-stone-500 leading-normal" style={DISPLAY}>
            {c.gapNumber}
          </span>
        )}{' '}
        {c.after}
      </div>
      {!typed && (
        <div className={`flex flex-col gap-2 ${stacked ? '' : 'lg:flex-row lg:items-center lg:gap-2.5'}`}>
          {!stacked && <span className="text-[17px] lg:text-[19px] font-bold lg:mr-1" style={DISPLAY}>{c.gapLabel}</span>}
          {c.options.map((o) => {
            const isAnswer = o.id === c.answer;
            const on = isAnswer && done;
            const hov = isAnswer && hover && !done;
            return (
              <div
                key={o.id}
                className={`flex items-center gap-2.5 px-3.5 rounded-xl transition-[background-color,border-color,color] duration-300 ${onPhone(o.id)} ${
                  stacked ? 'py-2 text-[13px] lg:text-[14px] leading-snug' : 'min-h-[46px] text-[15px]'
                }`}
                style={{
                  border: on ? '2px solid #006D77' : `1px solid ${hov ? '#A8A29E' : '#E7E5E4'}`,
                  background: on ? '#E6F2F3' : hov ? '#FAFAF9' : '#fff',
                  color: on ? '#00525A' : '#1C1917',
                  fontWeight: on ? 600 : 500,
                }}
              >
                <span className="w-4 h-4 shrink-0 rounded-full box-border" style={{ border: on ? '5px solid #006D77' : '2px solid #A8A29E' }} />
                <span className="text-[13px] font-bold text-stone-500" style={DISPLAY}>{o.id}</span>
                {o.text}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Question rows under an audio bar or a text: richtig/falsch rows and a/b/c
 * rows, answered one after the other.
 */
function QuestionsScene({ c, time, playing }) {
  const pill = (on) =>
    `inline-flex items-center min-h-[34px] px-3 rounded-full text-[12.5px] font-semibold border transition-colors duration-300 ${
      on ? 'bg-[#006D77] border-[#006D77] text-white' : 'bg-white border-stone-300 text-stone-500'
    }`;
  return (
    <div className="h-full flex flex-col">
      {c.audio && <AudioBar audio={c.audio} time={time} playing={playing} />}
      {c.reading && (
        <div className="rounded-2xl border border-stone-200 bg-stone-50 p-3.5 lg:p-4">
          {c.reading.title && <div className="text-[14px] lg:text-[15px] font-bold mb-1.5" style={DISPLAY}>{c.reading.title}</div>}
          <p className="text-[13px] lg:text-[14px] leading-relaxed text-stone-800 line-clamp-3 lg:line-clamp-none">{c.reading.text}</p>
        </div>
      )}
      {c.rows.map((r, i) => {
        const answered = time >= 14 + i * 13;
        return (
          <div key={r.num} className="flex flex-col gap-2 py-3 lg:py-3.5 border-b border-stone-100">
            <span className="text-[13.5px] lg:text-[15px] leading-normal">
              <b className="mr-1.5 text-stone-500" style={DISPLAY}>{r.num}</b>
              {r.text}
            </span>
            {r.options ? (
              <span className="flex flex-col lg:flex-row gap-1.5 lg:gap-2">
                {r.options.map((o) => (
                  <span key={o.id} className={`${pill(answered && o.id === r.answer)} lg:max-w-[33%]`}>
                    <b className="mr-1.5">{o.id}</b>
                    <span className="truncate">{o.text}</span>
                  </span>
                ))}
              </span>
            ) : (
              <span className="flex gap-2">
                {r.labels.map((label, k) => (
                  <span key={label} className={pill(answered && k === r.answer)}>{label}</span>
                ))}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Statements (or people) on one side, slots on the other, filled in turn. */
function MatchScene({ c, time, playing }) {
  const filled = c.rows.map((_, i) => time >= 12 + i * 12);
  const lit = c.rows.map((r, i) => (filled[i] ? r.answer : null)).filter(Boolean);
  const current = c.rows.find((_, i) => time >= 6 + i * 12 && !filled[i])?.answer;
  const onPhone = phoneKeep(c.options, c.rows.map((r) => r.answer), 1);
  return (
    <div className="h-full flex flex-col gap-3">
      {c.audio && <AudioBar audio={c.audio} time={time} playing={playing} />}
      <div className={`grid gap-3 lg:gap-5 ${c.rowsFirst ? 'lg:grid-cols-[1.1fr_1fr]' : 'lg:grid-cols-[1fr_1.1fr]'}`}>
        <div className={`flex flex-col gap-2 ${c.rowsFirst ? 'lg:order-1' : 'lg:order-2'}`}>
          {c.rows.map((r, i) => (
            <div key={r.label} className="flex items-center gap-3 py-1.5 border-b border-stone-100">
              <span className="flex-1 text-[13.5px] lg:text-[14.5px] leading-snug">{r.label}</span>
              <Slot filled={filled[i]} value={r.answer} size="w-9 h-9 text-[15px]" />
            </div>
          ))}
        </div>
        <div className={`flex flex-col gap-1.5 ${c.rowsFirst ? 'lg:order-2' : 'lg:order-1'}`}>
          {c.optionsLabel && <div className={`mb-0.5 ${LABEL}`}>{c.optionsLabel}</div>}
          {c.options.map((o) => (
            <OptionRow
              key={o.id}
              id={o.id}
              text={o.text}
              className={onPhone(o.id)}
              state={o.id === current ? 'hover' : lit.includes(o.id) && !c.reusable ? 'used' : lit.includes(o.id) ? 'picked' : 'idle'}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** ÖSD's cut-off right edge: each line is missing its last letters. */
function EndingsScene({ c, time }) {
  return (
    <div className="h-full flex flex-col gap-3">
      <div className={LABEL}>{c.label}</div>
      <div className="relative rounded-2xl border border-stone-200 bg-stone-50 py-3 pl-4 pr-0 overflow-hidden">
        {c.lines.map((l, i) => {
          const start = 6 + i * 5;
          const shown = l.answer.slice(0, Math.max(0, Math.min(l.answer.length, time - start)));
          return (
            <div key={i} className="grid grid-cols-[minmax(0,1fr)_74px] items-center border-b border-stone-200/70 last:border-0">
              {/* Wrap, never truncate: the line's last letters are the clue. */}
              <span className="py-1.5 pr-2 text-[13.5px] lg:text-[15px] leading-snug text-stone-800 text-right break-words">
                {l.text}
              </span>
              <span className="h-full flex items-center px-2 border-l-2 border-dashed border-stone-300 bg-white text-[14px] lg:text-[15px] font-bold text-[#006D77]" style={DISPLAY}>
                {shown}
                {shown.length > 0 && shown.length < l.answer.length && <span className="showcase-caret" />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WritingScene({ c, time }) {
  const typed = c.letter.slice(0, Math.min(c.letter.length, time * 7));
  const words = typed.trim() ? typed.trim().split(/\s+/).length : 0;
  return (
    <div className="h-full flex flex-col lg:grid lg:grid-cols-[0.8fr_1.2fr] gap-3 lg:gap-5">
      <div className="rounded-2xl border border-stone-200 bg-stone-50 p-3.5 lg:p-4 flex flex-col gap-2">
        <span className={LABEL}>{c.taskLabel}</span>
        <p className="text-[13px] lg:text-[14px] leading-relaxed text-stone-800 line-clamp-3 lg:line-clamp-none">{c.task}</p>
      </div>
      <div className="flex-1 min-h-0 flex flex-col rounded-2xl border border-stone-300 overflow-hidden">
        <div className="flex-1 min-h-0 overflow-hidden p-3.5 lg:p-4 text-[14px] lg:text-[15px] leading-relaxed whitespace-pre-wrap">
          {typed}
          <span className="showcase-caret" />
        </div>
        <div className="px-3.5 py-2 border-t border-stone-200 bg-stone-50 text-right text-[12px] font-semibold text-stone-500 tabular-nums">
          {words} Wörter{c.wordGoal ? ` · ${c.wordGoal}` : ''}
        </div>
      </div>
    </div>
  );
}

/**
 * A sample result, labelled "Beispiel". `pass` on a bar marks a real minimum for
 * that part (Goethe modules, ÖSD B2/C1 subtests); exams that only count the
 * total (telc, ÖSD B1) have none, so no bar suggests a minimum that isn't there.
 */
function ResultsScene({ c, time }) {
  const { t } = useTranslation();
  const k = easeOut(time / 15);
  const got = c.bars.reduce((a, r) => a + r.got, 0);
  const max = c.bars.reduce((a, r) => a + r.max, 0);
  const score = Math.round(got * k);
  return (
    <div className="h-full flex flex-col lg:grid lg:grid-cols-[1.1fr_1fr] gap-4 lg:gap-6 content-start">
      <div className="flex flex-col gap-3">
        {c.showTotal ? (
          <div className="flex items-baseline gap-2">
            <span className="text-[3.25rem] lg:text-[4rem] leading-[0.9] font-bold tabular-nums" style={NUMERAL}>{score}</span>
            <span className="text-[18px] lg:text-[20px] font-bold text-stone-500" style={DISPLAY}>/ {max}</span>
            <span className="ml-auto text-[10px] lg:text-[11px] font-semibold uppercase tracking-[0.1em] text-stone-500">Beispiel</span>
          </div>
        ) : (
          <div className="flex items-baseline justify-between">
            <span className="text-[22px] lg:text-[26px] font-bold" style={DISPLAY}>{c.heading}</span>
            <span className="text-[10px] lg:text-[11px] font-semibold uppercase tracking-[0.1em] text-stone-500">Beispiel</span>
          </div>
        )}
        <span className="self-start inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-800 text-[12px] lg:text-[13px] font-bold">
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
          {c.verdict.replace('{{pct}}', Math.round((score / max) * 100)).replace('{{score}}', score)}
        </span>
        {c.bars.map((r) => (
          <div key={r.name} className="flex flex-col gap-1">
            <div className="flex justify-between text-[12px] lg:text-[13px]">
              <span className="font-semibold">{r.name}</span>
              <span className="text-stone-500 tabular-nums">{Math.round(r.got * k)} / {r.max}</span>
            </div>
            <div className="relative h-[7px] rounded-full bg-stone-100">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-stone-900 origin-left transition-transform duration-[900ms] ease-[cubic-bezier(.2,.7,.2,1)]"
                style={{ width: `${Math.round((r.got / r.max) * 100)}%`, transform: `scaleX(${time >= 2 ? 1 : 0})` }}
              />
              {r.pass != null && (
                <span className="absolute -top-[3px] w-0.5 h-[13px] bg-stone-900/40" style={{ left: `${r.pass * 100}%` }} />
              )}
            </div>
          </div>
        ))}
        {c.barsNote && <span className="text-[11.5px] text-stone-500 leading-snug">{t('mockExam.showcase.barsNote', { defaultValue: c.barsNote })}</span>}
      </div>
      <div className="self-start rounded-2xl border border-stone-200 bg-stone-50 p-3.5 lg:p-5 flex flex-col gap-2">
        <div className="flex justify-between items-center gap-3">
          <span className="text-[15px] font-bold" style={DISPLAY}>{c.explanation.label}</span>
          <span className="text-[13px] font-semibold text-[#006D77]">Ihre Antwort: {c.explanation.answer}</span>
        </div>
        <p className="text-[13px] lg:text-[14px] leading-relaxed text-stone-800">{c.explanation.text}</p>
      </div>
    </div>
  );
}

const SCENES = {
  matching: MatchingScene,
  gap: GapScene,
  questions: QuestionsScene,
  match: MatchScene,
  endings: EndingsScene,
  writing: WritingScene,
  results: ResultsScene,
};

/* ── Stage ────────────────────────────────────────────────────────────────── */

/**
 * @param {object}    props.exam      Catalog entry (src/data/mockExamCatalog.js).
 * @param {object}    props.showcase  Entry from src/data/examShowcase.js.
 * @param {ReactNode} props.hero      Chapter 00 caption: breadcrumb, h1, intro.
 * @param {ReactNode} props.cta       Primary + secondary CTA, shown on every chapter.
 */
export default function ExamShowcase({ exam, showcase, hero, cta }) {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const chapters = showcase.chapters;
  const count = chapters.length + 1; // + chapter 00
  const markers = useRef([]);
  const [active, setActive] = useState(0);
  const [tick, setTick] = useState(0);

  // Which chapter: the last marker whose top has crossed the middle of the viewport.
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const line = window.innerHeight * 0.5;
      let next = 0;
      markers.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top < line) next = i;
      });
      setActive(next);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Each chapter's little performance plays once from the moment it becomes active.
  useEffect(() => {
    setTick(0);
    if (reduced) return undefined;
    const id = setInterval(() => {
      setTick((v) => {
        if (v >= TICK_MAX) {
          clearInterval(id);
          return v;
        }
        return v + 1;
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [active, reduced]);

  /** Local time of chapter i: finished if already passed, not started if ahead. */
  const timeOf = (i) => (reduced ? 999 : i === active ? tick : i < active ? 999 : 0);

  const jumpTo = useCallback(
    (i) => {
      const el = markers.current[i];
      if (!el) return;
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY + 1,
        behavior: reduced ? 'auto' : 'smooth',
      });
    },
    [reduced],
  );

  const current = active === 0 ? null : chapters[active - 1];
  const clock =
    active === 0
      ? `${exam.totalMinutes} min`
      : current.clockSeconds == null
        ? 'Fertig'
        : mmss(current.clockSeconds - Math.floor(Math.min(tick, TICK_MAX) / 10));
  const navLabels = [
    t('mockExam.showcase.overview', { defaultValue: 'Overview' }),
    ...chapters.map((c) => c.nav),
  ];

  return (
    <section
      className="relative"
      // One 80svh marker per chapter, plus some dwell on the last one before the
      // stage scrolls away with the end of this section.
      style={{ height: `${count * MARKER_SVH + 20}svh` }}
      aria-label={t('mockExam.showcase.label', { examName: exam.examName, defaultValue: 'Inside the {{examName}} mock exam' })}
    >
      {/* Scroll distance: the stage below reads where these markers are. They sit
          behind it out of flow, so the stage is the section's only in-flow child
          and stops sticking exactly where the section ends. */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 pointer-events-none">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} ref={(el) => { markers.current[i] = el; }} style={{ height: `${MARKER_SVH}svh` }} />
        ))}
      </div>

      <div className="sticky top-14 z-10 bg-stone-50 text-stone-900" style={{ height: STAGE_H }}>
        <div className="h-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 pt-3 pb-3 lg:py-10 flex flex-col gap-3 lg:grid lg:grid-cols-[150px_minmax(0,1fr)_minmax(0,600px)] lg:gap-12 lg:items-center">

          {/* Chapter index (desktop). Real buttons: optional, the page never needs a click. */}
          <nav aria-label={t('mockExam.showcase.navLabel', { defaultValue: 'Exam parts' })} className="hidden lg:block relative">
            <div aria-hidden="true" className="absolute left-[5px] top-3 bottom-3 w-0.5 rounded bg-stone-200 overflow-hidden">
              <div
                className="h-full bg-[#d97706] origin-top transition-transform duration-[900ms] ease-[cubic-bezier(.2,.7,.2,1)]"
                style={{ transform: `scaleY(${active / (count - 1)})` }}
              />
            </div>
            <ol className="flex flex-col gap-1.5">
              {navLabels.map((label, i) => (
                <li key={label}>
                  <button
                    type="button"
                    onClick={() => jumpTo(i)}
                    aria-current={i === active ? 'step' : undefined}
                    className={`flex items-center gap-3 min-h-[40px] text-left text-[13px] cursor-pointer transition-colors duration-300 hover:text-stone-900 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-stone-900 ${
                      i === active ? 'font-bold text-stone-900' : 'font-medium text-stone-500'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className="relative z-[1] shrink-0 w-3 h-3 rounded-full box-border transition-all duration-400"
                      style={
                        i <= active
                          ? { background: '#d97706', border: '3px solid #FAFAF9', boxShadow: '0 0 0 1px #d97706' }
                          : { background: '#FAFAF9', border: '2px solid #D6D3D1' }
                      }
                    />
                    {label}
                  </button>
                </li>
              ))}
            </ol>
          </nav>

          {/* Progress (phone) */}
          <div aria-hidden="true" className="lg:hidden flex gap-1 px-1">
            {chapters.map((c, i) => (
              <span key={c.id} className="flex-1 h-[3px] rounded-full transition-colors duration-400" style={{ background: i + 1 <= active ? '#d97706' : '#E7E5E4' }} />
            ))}
          </div>

          {/* Captions: every chapter's text is in the DOM, only the active one is visible. */}
          <div className={`relative shrink-0 h-[156px] max-lg:[@media(max-height:700px)]:h-[124px] lg:h-[440px] px-1 lg:px-0`}>
            <div className={`absolute inset-x-1 lg:inset-x-0 top-0 flex flex-col gap-2 lg:gap-5 ${LAYER_TRANSITION}`} style={layerStyle(0, active)}>
              {hero}
            </div>
            {chapters.map((c, i) => (
              <div key={c.id} className={`absolute inset-x-1 lg:inset-x-0 top-0 flex flex-col gap-1.5 lg:gap-5 ${LAYER_TRANSITION}`} style={layerStyle(i + 1, active)}>
                <span className="text-[12px] lg:text-[13px] font-bold tracking-[0.08em] text-amber-700" style={DISPLAY}>
                  0{i + 1} · {c.nav}
                </span>
                <h2 className={`text-[26px] max-lg:[@media(max-height:700px)]:text-[22px] lg:text-[44px] leading-[1.08] lg:leading-[1.05] font-bold tracking-tight text-stone-900`} style={DISPLAY}>
                  {t(`mockExam.showcase.${exam.slug}.${c.id}.title`, { defaultValue: c.title })}
                </h2>
                <p className={`text-[14px] lg:text-[16px] leading-normal lg:leading-relaxed text-stone-500 max-w-md line-clamp-3 max-lg:[@media(max-height:700px)]:line-clamp-2 lg:line-clamp-none`}>
                  {t(`mockExam.showcase.${exam.slug}.${c.id}.body`, { defaultValue: c.body })}
                </p>
              </div>
            ))}
            <div className="hidden lg:flex absolute left-0 bottom-0 items-center gap-6">{cta}</div>
          </div>

          {/* The exam frame: decorative replica of the simulator. */}
          <div
            aria-hidden="true"
            className="flex-1 min-h-0 lg:flex-none lg:h-[min(600px,calc(100svh-10rem))] flex flex-col overflow-hidden rounded-[22px] lg:rounded-3xl border border-stone-200 bg-white shadow-[0_1px_0_rgba(28,25,23,.04),0_40px_80px_-40px_rgba(28,25,23,.4)]"
          >
            <div className="flex items-center justify-between gap-4 px-4 lg:px-5 py-3 border-b-4 border-black">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-6 h-6 lg:w-7 lg:h-7 shrink-0 rounded-lg bg-black text-white inline-flex items-center justify-center text-[12px] lg:text-[14px] font-bold" style={DISPLAY}>L</span>
                <span className="text-[12px] lg:text-[13px] font-semibold truncate">{current ? current.section : t(`mockExam.showcase.${exam.slug}.paperLabel`, { defaultValue: showcase.paperLabel })}</span>
              </div>
              <span className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 text-[14px] lg:text-[15px] font-bold tabular-nums" style={DISPLAY}>
                <Clock className="w-4 h-4" />
                {clock}
              </span>
            </div>
            <div className="hidden lg:flex gap-1 px-5 pt-2.5">
              {chapters.map((c, i) => (
                <span key={c.id} className="flex-1 h-[3px] rounded-full transition-colors duration-400" style={{ background: i + 1 <= active ? '#1C1917' : '#E7E5E4' }} />
              ))}
            </div>
            <div className="relative flex-1 min-h-0 overflow-hidden">
              <div data-showcase-layer={0} className={`absolute inset-0 p-4 lg:p-6 ${LAYER_TRANSITION}`} style={layerStyle(0, active)}>
                <FitBox><CoverScene exam={exam} t={t} time={timeOf(0)} /></FitBox>
              </div>
              {chapters.map((c, i) => {
                const Scene = SCENES[c.scene];
                return (
                  <div key={c.id} data-showcase-layer={i + 1} className={`absolute inset-0 p-4 lg:p-6 ${LAYER_TRANSITION}`} style={layerStyle(i + 1, active)}>
                    <FitBox><Scene c={c} time={timeOf(i + 1)} playing={active === i + 1 && !reduced} /></FitBox>
                  </div>
                );
              })}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent" />
            </div>
          </div>

          <div className="lg:hidden shrink-0">{cta}</div>
        </div>
      </div>
    </section>
  );
}
